import React, { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { CatalogRecord, FormValues, ResourceName } from "./CatalogAdmin.types.tsx";
import FieldInput from "./FieldInput.tsx";
import { failureMessages, parseVolts, problemOf, toFields, toFormValues } from "./model.tsx";
import { resourceSpec } from "./resources.tsx";

import { createRecord, updateRecord } from "../../services/catalog.services.tsx";

interface RecordModalProps {
  resource: ResourceName;
  record: CatalogRecord | null;
  onHide: () => void;
  onSaved: () => void;
}

const RecordModal: React.FC<RecordModalProps> = ({ resource, record, onHide, onSaved }) => {
  const { t } = useTranslation();
  const spec = resourceSpec(resource);
  const [values, setValues] = useState<FormValues>(() =>
    toFormValues(spec.fields, record?.fields ?? null)
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [messages, setMessages] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const isField = (key: string) => spec.fields.some((field) => field.name === key);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const invalid = spec.fields.filter(
      (field) => field.kind === "integers" && parseVolts(values[field.name] ?? "") === null
    );

    if (invalid.length > 0) {
      setFieldErrors(
        Object.fromEntries(invalid.map((field) => [field.name, [t("catalog.form.integersOnly")]]))
      );
      return;
    }

    setBusy(true);

    try {
      const fields = toFields(spec.fields, values);

      if (record) {
        await updateRecord(resource, record.id, fields);
      } else {
        await createRecord(resource, fields);
      }

      onSaved();
    } catch (error) {
      setFieldErrors(problemOf(error).errors);
      setMessages(failureMessages(error, isField, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show onHide={onHide} centered className="catalog-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t(record ? "catalog.form.editTitle" : "catalog.form.newTitle", {
              resource: t(`catalog.resources.${resource}`),
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {messages.map((message) => (
            <Alert key={message} variant="danger">
              {message}
            </Alert>
          ))}
          {spec.fields.map((field) => (
            <FieldInput
              key={field.name}
              scope="record"
              field={field}
              value={values[field.name] ?? ""}
              errors={fieldErrors[field.name]}
              onChange={(value) => setValues({ ...values, [field.name]: value })}
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t("catalog.form.cancel")}
          </Button>
          <Button type="submit" variant="success" disabled={busy}>
            {t("catalog.form.save")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RecordModal;
